import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import { createClient, Video } from "pexels";
import { ReactComponent as PexelSvg } from "images/svg/pexels.svg";
import Modal from "components/modal";
import ClipCanvasVideo from "components/modal/modals/clipCanvasVideo";
import Spinner from "components/spinner";

const PexelsVideos = ({ searchText }: { searchText: string }) => {
  const PexelsApiKey = process.env.REACT_APP_PIXELS_API;
  const [videos, setVideos] = useState<Video[]>([]);
  const [orientation, setOrientation] = useState("");
  const client = createClient(PexelsApiKey || "");

  const [modalOpen, setModalOpen] = useState(false);
  const [videoId, setVideoId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(1);
  const isLoadingRef = useRef(false);
  const searchTextRef = useRef(searchText);
  const hasMoreRef = useRef(true);

  const OnClickVideo = (id: number) => {
    setModalOpen(true);
    setVideoId(id);
  };

  const CloseModal = () => {
    setModalOpen(false);
    setVideoId(0);
  };

  const GetPopularVideos = useCallback(
    async (pageNum: number, append: boolean = false) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      try {
        setLoading(true);
        const response = await client.videos.popular({
          orientaion: orientation ? orientation : undefined,
          per_page: 10,
          page: pageNum
        });
        if ("videos" in response) {
          if (append) {
            setVideos((prev) => [...prev, ...response.videos]);
          } else {
            setVideos(response.videos);
          }
          // Check if there are more pages (Pexels API typically returns 8000 max results)
          const hasMorePages =
            response.videos.length === 10 && response.page < 400;
          setHasMore(hasMorePages);
          hasMoreRef.current = hasMorePages;
        }
      } catch (error) {
        console.error("Failed to fetch popular videos from Pexels:", error);
        setHasMore(false);
        hasMoreRef.current = false;
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    },
    [client, orientation]
  );

  const SearchVideos = useCallback(
    async (pageNum: number, append: boolean = false) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      try {
        setLoading(true);
        const response = await client.videos.search({
          query: searchText,
          orientaion: orientation ? orientation : undefined,
          per_page: 10,
          page: pageNum
        });
        if ("videos" in response) {
          if (append) {
            setVideos((prev) => [...prev, ...response.videos]);
          } else {
            setVideos(response.videos);
          }
          // Check if there are more pages
          const hasMorePages =
            response.videos.length === 10 &&
            response.page < response.total_results / 20;
          setHasMore(hasMorePages);
          hasMoreRef.current = hasMorePages;
        }
      } catch (error) {
        console.error("Failed to fetch videos from Pexels:", error);
        setHasMore(false);
        hasMoreRef.current = false;
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    },
    [client, searchText, orientation]
  );

  // Load more function
  const handleLoadMore = useCallback(() => {
    if (isLoadingRef.current || !hasMoreRef.current) return;

    pageRef.current += 1;

    if (searchTextRef.current === "") {
      GetPopularVideos(pageRef.current, true);
    } else {
      SearchVideos(pageRef.current, true);
    }
  }, [GetPopularVideos, SearchVideos]);

  // Update refs when values change
  useEffect(() => {
    searchTextRef.current = searchText;
  }, [searchText]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Initial fetch
  useEffect(() => {
    pageRef.current = 1;
    setHasMore(true);
    hasMoreRef.current = true;
    isLoadingRef.current = false;

    GetPopularVideos(1, false);
  }, []);

  useEffect(() => {
    if (searchText !== "") {
      SearchVideos(1, false);
    } else {
      GetPopularVideos(1, false);
    }
  }, [searchText, GetPopularVideos, SearchVideos]);

  const LoadingState = () => {
    return (
      <VideoGrid>
        <VideoContainer
          orientation="portrait"
          className={`grid-item portrait`}
        />
        <VideoContainer
          orientation="landscape"
          className={`grid-item landscape`}
        />
        <VideoContainer
          orientation="portrait"
          className={`grid-item portrait`}
        />
        <VideoContainer orientation="square" className={`grid-item square`} />
        <VideoContainer
          orientation="landscape"
          className={`grid-item landscape`}
        />
      </VideoGrid>
    );
  };

  return (
    <>
      <Container>
        <Header>
          <PexelsLogo>
            <PexelSvg width={"100%"} />
          </PexelsLogo>
          <OrientationsContainer>
            <OrientationItem
              active={orientation === "portrait" ? "true" : "false"}
              onClick={() => {
                if (orientation === "portrait") {
                  setOrientation("");
                  return;
                }
                setOrientation("portrait");
              }}
            >
              <OrientationIcon orientation={"portrait"} />
            </OrientationItem>
            <OrientationItem
              active={orientation === "landscape" ? "true" : "false"}
              onClick={() => {
                if (orientation === "landscape") {
                  setOrientation("");
                  return;
                }
                setOrientation("landscape");
              }}
            >
              <OrientationIcon orientation={"landscape"} />
            </OrientationItem>
            {/* <OrientationItem
              active={orientation === "square" ? "true" : "false"}
              onClick={() => {
                if (orientation === "square") {
                  setOrientation("");
                  return;
                }
                setOrientation("square");
              }}
            >
              <OrientationIcon orientation={"square"} />
            </OrientationItem> */}
          </OrientationsContainer>
        </Header>
        {videos.length > 0 ? (
          <>
            <VideoGrid>
              {videos.map((video, index) => {
                const { width, height } = video.video_files[0] || {};
                const videoOrientation =
                  width && height
                    ? width > height * 1.2
                      ? "landscape"
                      : height > width * 1.2
                      ? "portrait"
                      : "square"
                    : "landscape";

                if (orientation !== videoOrientation && orientation !== "") {
                  return null;
                }

                return (
                  <VideoContainer
                    key={video.id || index}
                    orientation={videoOrientation}
                    className={`grid-item ${videoOrientation}`}
                    onClick={() => OnClickVideo(video.id)}
                  >
                    <ThumbnailImage
                      src={video.image}
                      alt={`Video thumbnail ${index + 1}`}
                    />
                    <HoverVideo
                      src={video.video_files[0]?.link}
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  </VideoContainer>
                );
              })}
            </VideoGrid>
            {hasMore && (
              <LoadMoreContainer>
                {loading ? (
                  <Spinner width="2rem" />
                ) : (
                  <LoadMoreButton onClick={handleLoadMore}>
                    Load More Videos
                  </LoadMoreButton>
                )}
              </LoadMoreContainer>
            )}
          </>
        ) : (
          <LoadingState />
        )}
      </Container>
      {modalOpen && (
        <Modal isActive={modalOpen} closeModal={() => CloseModal()}>
          <ClipCanvasVideo videoId={videoId} onClose={() => CloseModal()} />
        </Modal>
      )}
    </>
  );
};

export default PexelsVideos;

// Styled Components
const Container = styled.div`
  width: 100%;
  margin-top: 2.4rem;
  padding: 0 1rem;
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 1.6rem;
  border-bottom: 1px solid ${Colors.grey600};
`;
const PexelsLogo = styled.div`
  width: 6.3rem;
`;

const OrientationsContainer = styled.div`
  padding: 0.3rem;
  border-radius: 8px;
  border: 1px solid ${Colors.grey600};
  display: flex;
`;

const OrientationItem = styled.div<{ active: string }>`
  padding: 0.5rem;
  border-radius: 7px;
  background-color: ${(props) =>
    props.active === "true" ? Colors.grey600 : ""};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OrientationIcon = styled.div<{ orientation: string }>`
  width: ${(props) => (props.orientation === "landscape" ? "2.4rem" : "2rem")};
  height: ${(props) => (props.orientation === "portrait" ? "2.4rem" : "2rem")};
  border: 1px solid white;
  border-radius: 4px;
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: 2.4rem 0;

  @media (max-width: 1024px) {
    gap: 12px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const VideoContainer = styled.div<{ orientation: string }>`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: ${Colors.grey700};
  cursor: pointer;
  transition: transform 0.2s ease;
  grid-row-end: span
    ${({ orientation }) =>
      orientation === "portrait" ? 32 : orientation === "square" ? 20 : 18};

  /* @media (max-width: 768px) {
    grid-row-end: span
      ${({ orientation }) =>
    orientation === "portrait" ? 32 : orientation === "square" ? 20 : 18};
  } */

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    video {
      opacity: 1;
    }
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const HoverVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.3s ease;
`;

const LoadMoreContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
  margin-top: 2rem;
`;

const LoadMoreButton = styled.button`
  background: ${Colors.grey700};
  color: ${Colors.white100};
  border: 1px solid ${Colors.grey600};
  border-radius: 8px;
  padding: 1.2rem 3rem;
  font-size: 1.4rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${Colors.grey600};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;
